import { Component } from '@theme/component';

const FORMS_TAG = 'shopify-forms-embed';
const SHADOW_RETRIES = 20;
const SHADOW_RETRY_DELAY = 250;

/**
 * Shared stylesheet adopted into each Shopify Forms shadow root. Custom properties set
 * on the host by <skrey-forms-style> (and the theme's color context) inherit through
 * the shadow boundary, so per-block values stay out of this static sheet.
 * The app ships its own high-specificity rules, hence the !important overrides.
 */
const FORMS_CSS = `
  form div:empty,
  form div:has(> shop-lead-capture) {
    display: none !important;
  }

  input:not([type='checkbox'], [type='radio'], [type='hidden']),
  textarea,
  select {
    background-color: var(--skrey-forms-field-background, #fff) !important;
    color: var(--skrey-forms-field-text, #121212) !important;
    border: 1px solid var(--skrey-forms-field-border, #d4d4d4) !important;
    border-radius: var(--skrey-forms-field-radius, 8px) !important;
    box-shadow: none !important;
    font-family: var(--font-body--family) !important;
    letter-spacing: 0.06em;
  }

  input:not([type='checkbox'], [type='radio'], [type='hidden']):focus-visible,
  textarea:focus-visible,
  select:focus-visible {
    outline: 2px solid var(--skrey-forms-field-text, #121212) !important;
    outline-offset: -2px;
  }

  label,
  input::placeholder,
  textarea::placeholder {
    color: var(--skrey-forms-field-label, #8b8b8b) !important;
    font-family: var(--font-body--family) !important;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  label:has(input[type='checkbox']) {
    color: inherit !important;
    text-transform: none;
    letter-spacing: normal;
  }

  /* Block + auto margins align the button in grid, flex, or block form layouts. */
  button[type='submit'] {
    display: block !important;
    width: fit-content !important;
    margin-inline: var(--skrey-forms-button-margin, auto) !important;
    justify-self: auto !important;
    align-self: auto !important;
    padding: 0.75rem 1.25rem !important;
    background-color: transparent !important;
    color: var(--color-foreground) !important;
    border: 1px solid currentColor !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    font-family: var(--font-body--family) !important;
    font-size: 1rem !important;
    text-transform: uppercase !important;
    letter-spacing: 0.08em !important;
    transition: background-color var(--animation-speed, 0.2s) ease, color var(--animation-speed, 0.2s) ease;
  }

  button[type='submit']:hover {
    background-color: var(--color-foreground) !important;
    color: var(--color-background) !important;
  }

  button[type='submit']:focus-visible {
    outline: 1px solid var(--color-foreground) !important;
    outline-offset: 3px;
  }

  @media screen and (max-width: 749px) {
    button[type='submit'] {
      margin-inline: var(--skrey-forms-button-margin-mb, 0 auto) !important;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    button[type='submit'] {
      transition: none;
    }
  }
`;

/** @type {CSSStyleSheet | null} */
let sharedSheet = null;

function getSheet() {
  if (!sharedSheet) {
    sharedSheet = new CSSStyleSheet();
    sharedSheet.replaceSync(FORMS_CSS);
  }
  return sharedSheet;
}

/**
 * <skrey-forms-style> — restyles Shopify Forms embeds nested inside it, or in its
 * section when none are nested. The embed renders asynchronously into an open
 * shadow root, so embeds are picked up as they appear.
 *
 * @extends {Component}
 */
export class SkreyFormsStyle extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {WeakSet<Element>} */
  #styled = new WeakSet();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const scope = this.#getScope();

    this.#styleEmbeds(scope);

    this.#observer = new MutationObserver(() => this.#styleEmbeds(this.#getScope()));
    this.#observer.observe(scope, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /** Nested forms win; otherwise fall back to the surrounding section. */
  #getScope() {
    if (this.querySelector(FORMS_TAG)) return this;
    return this.closest('.shopify-section') ?? this;
  }

  /** @param {Element} scope */
  #styleEmbeds(scope) {
    for (const embed of scope.querySelectorAll(FORMS_TAG)) {
      if (this.#styled.has(embed)) continue;
      this.#styled.add(embed);
      this.#applyVariables(embed);
      this.#adoptWhenReady(embed);
    }
  }

  /**
   * Copies this block's custom properties onto the host so they reach the shadow
   * tree even when the embed is a sibling rather than a descendant.
   * @param {Element} embed
   */
  #applyVariables(embed) {
    if (!(embed instanceof HTMLElement) || this.contains(embed)) return;

    for (const property of this.style) {
      if (property.startsWith('--skrey-forms-')) {
        embed.style.setProperty(property, this.style.getPropertyValue(property));
      }
    }
  }

  /** @param {Element} embed */
  async #adoptWhenReady(embed) {
    const signal = this.#abortController?.signal;

    await customElements.whenDefined(FORMS_TAG);

    for (let attempt = 0; attempt < SHADOW_RETRIES; attempt++) {
      if (signal?.aborted) return;

      const { shadowRoot } = embed;
      if (shadowRoot) {
        const sheet = getSheet();
        if (!shadowRoot.adoptedStyleSheets.includes(sheet)) {
          shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet];
        }
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, SHADOW_RETRY_DELAY));
    }

    // Closed or never-attached shadow root: leave the app's default styles.
    this.#styled.delete(embed);
  }
}

if (!customElements.get('skrey-forms-style')) {
  customElements.define('skrey-forms-style', SkreyFormsStyle);
}
